import { Box, Card, Divider, Flex, Group, Menu, Text } from '@mantine/core';
import { FC } from 'react';
import { useNavigate } from 'react-router';
import type { Page } from '../../../api/pages-client/pages-client.type';
import { newCOLORS } from '../../../styles/colors';
import { baseBlack, baseWhite, secondaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { H2 } from '../../../ui-library/typography/typography';

interface DashboardHeaderProps {
  selectedDashboard: string;
  onDashboardChange: (dashboardId: string) => void;
  onCreateDashboard: () => void;
  onDeleteDashboard: (dashboardId: string) => void;
  dashboards: Page[];
}

export const DashboardHeader: FC<DashboardHeaderProps> = ({
  selectedDashboard,
  onDashboardChange,
  onCreateDashboard,
  onDeleteDashboard,
  dashboards,
}) => {
  const selectedDashboardData = dashboards.find((d) => d.id === selectedDashboard);
  const navigate = useNavigate();
  return (
    <Flex align="center" gap="md" mb="lg">
      <Group gap={8} ml="1rem">
        <Button
          variant="secondary"
          radius="xl"
          size="sm"
          leftSection={<Icon name="arrow_back" size={20} color={baseWhite} />}
          onClick={() => navigate('/application/dashboards')}
        >
          Back
        </Button>

        <Icon name="dashboard" size={20} color={secondaryBase} />
        <H2>{selectedDashboardData?.name || 'Select Dashboard'}</H2>
        <Menu shadow="md" width={400} position="bottom-start">
          <Menu.Target>
            <Button variant="subtle" size="sm" px={4} style={{ color: 'inherit' }}>
              <Icon name="expand_circle_down" size={20} color={baseBlack} pointer />
            </Button>
          </Menu.Target>
          <Menu.Dropdown p="large" style={{ padding: '1rem', marginTop: '5rem', width: '35%', left: '310px' }}>
            <Flex direction="column" gap="sm" style={{ padding: '1rem' }}>
              {dashboards.map((dashboard) => (
                <Card
                  key={dashboard.id}
                  withBorder
                  padding="md"
                  radius="md"
                  onClick={() => dashboard.id && onDashboardChange(dashboard.id)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: dashboard.id === selectedDashboard ? newCOLORS.lighterPurple : undefined,
                    borderColor: dashboard.id === selectedDashboard ? newCOLORS.darkPurple : undefined,
                  }}
                >
                  <Flex align="center" justify="space-between">
                    <Flex align="center" gap={8}>
                      <Box
                        style={{
                          width: '16px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Icon
                          name="check"
                          size={16}
                          style={{
                            color: dashboard.id === selectedDashboard ? newCOLORS.darkPurple : 'transparent',
                            visibility: dashboard.id === selectedDashboard ? 'visible' : 'hidden',
                          }}
                        />
                      </Box>
                      <div>
                        <Text fw={500}>{dashboard.name}</Text>
                        <Text size="sm" c="dimmed" mt={4}>
                          {dashboard.description}
                        </Text>
                      </div>
                    </Flex>
                    {dashboards.length > 1 && (
                      <Button
                        variant="subtle"
                        color="red"
                        size="sm"
                        px={4}
                        onClick={(e) => {
                          e.stopPropagation();
                          dashboard.id && onDeleteDashboard(dashboard.id);
                        }}
                        style={{
                          visibility: dashboard.id === selectedDashboard ? 'visible' : 'hidden',
                        }}
                      >
                        <Icon name="delete" size={20} style={{ color: newCOLORS.red }} />
                      </Button>
                    )}
                  </Flex>
                </Card>
              ))}
              <Divider my="sm" />
              <Button
                variant="outline"
                rightSection={<Icon name="add" size={16} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateDashboard();
                }}
                fullWidth
              >
                Create New Dashboard
              </Button>
            </Flex>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Flex>
  );
};
